import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Add, Trash, InfoCircle } from "iconsax-react";
import { Button, Input, Select, SearchableSelect, ToggleButton, Tooltip, Toast } from "@/shared/ui";
import { FileUploader } from "@/shared/components";
import { useDocumentSuggest } from "@/shared/hooks";
import { type Locale } from "@/shared/utils";
import type { EmploymentRecordItem } from "@/shared/api/documentSuggest";
import { MONTHS_RU, MONTHS_KK, MONTHS_EN } from "@/shared/consts/months";
import { EXPERIENCE_SECTOR_OPTIONS } from "@/features/hr/hiring";
import type { ExperienceRecord } from "../../../types/personalSheet";
import { editExperienceEntrySchema } from "./validation";
import { mapEtkItemToRow, experienceDedupKey } from "./experienceOcr";
import type { SectionFormHandle } from "./types";

const WORK_FORMAT_VALUES = ["onsite", "remote", "hybrid"] as const;

interface YearOption {
  label: string;
  value: number;
}

function generateYearOptions(): YearOption[] {
  const currentYear = new Date().getFullYear();
  const years: YearOption[] = [];
  for (let y = currentYear; y >= 1970; y--) {
    years.push({ label: String(y), value: y });
  }
  return years;
}

const YEAR_OPTIONS = generateYearOptions();

function getMonthOptions(locale: Locale) {
  const months = locale === "kk" ? MONTHS_KK : locale === "en" ? MONTHS_EN : MONTHS_RU;
  return months.map((name, idx) => ({ label: name, value: idx + 1 }));
}

function parseMonthYear(dateStr: string | null | undefined): { month: number | null; year: number | null } {
  if (!dateStr) return { month: null, year: null };
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return { month: null, year: null };
  return { month: d.getMonth() + 1, year: d.getFullYear() };
}

function buildISODate(month: number, year: number): string {
  return `${year}-${String(month).padStart(2, "0")}-01`;
}

interface Props {
  records: ExperienceRecord[];
  recordsEditable?: boolean;
  initialHasNoExperience?: boolean;
  workProofUrl?: string | null;
  enpfUrl?: string | null;
  sheetIin?: string;
  draftAddedRecords?: ExperienceRecord[];
}

interface ExperienceEntry {
  id: number | null;
  organization_name: string;
  speciality: string;
  work_position: string;
  work_format: string;
  work_address: string;
  begin_month: number | null;
  begin_year: number | null;
  end_month: number | null;
  end_year: number | null;
  is_current: boolean;
}

function entryBeginDate(entry: ExperienceEntry): string {
  return entry.begin_month && entry.begin_year ? buildISODate(entry.begin_month, entry.begin_year) : "";
}

function entryEndDate(entry: ExperienceEntry): string {
  return entry.end_month && entry.end_year ? buildISODate(entry.end_month, entry.end_year) : "";
}

function makeEmptyEntry(): ExperienceEntry {
  return {
    id: null,
    organization_name: "",
    speciality: "",
    work_position: "",
    work_format: "",
    work_address: "",
    begin_month: null,
    begin_year: null,
    end_month: null,
    end_year: null,
    is_current: false,
  };
}

function fromRecord(record: ExperienceRecord): ExperienceEntry {
  const begin = parseMonthYear(record.beginning_date);
  const end = parseMonthYear(record.end_date);
  return {
    id: record.id,
    organization_name: record.organization_name,
    speciality: record.speciality,
    work_position: record.work_position,
    work_format: record.work_format,
    work_address: record.work_address ?? "",
    begin_month: begin.month,
    begin_year: begin.year,
    end_month: end.month,
    end_year: end.year,
    is_current: record.is_current,
  };
}

function itemToEntry(item: EmploymentRecordItem): ExperienceEntry {
  return { id: null, speciality: "", work_format: "", work_address: "", ...mapEtkItemToRow(item) };
}

function entryDedupKey(entry: ExperienceEntry): string {
  return experienceDedupKey(entry.organization_name, entry.begin_year, entry.begin_month);
}

function isFilledEntry(entry: ExperienceEntry): boolean {
  return (
    entry.organization_name.trim() !== "" ||
    entry.speciality.trim() !== "" ||
    entry.work_position.trim() !== "" ||
    entry.work_format.trim() !== "" ||
    entry.work_address.trim() !== ""
  );
}

function toApiEntry(entry: ExperienceEntry): Record<string, unknown> {
  const api: Record<string, unknown> = {
    organization_name: entry.organization_name,
    speciality: entry.speciality,
    work_position: entry.work_position,
    work_format: entry.work_format,
    work_address: entry.work_address,
    beginning_date: entryBeginDate(entry),
    end_date: entry.is_current ? null : (entryEndDate(entry) || null),
    is_current: entry.is_current,
  };
  if (entry.id != null) api.id = entry.id;
  return api;
}

function entriesSignature(entries: ExperienceEntry[]): string {
  return JSON.stringify(entries.filter(isFilledEntry).map(toApiEntry));
}

const EditExperienceSection = forwardRef<SectionFormHandle, Props>(function EditExperienceSection(
  { records, recordsEditable = false, initialHasNoExperience, workProofUrl, enpfUrl, sheetIin, draftAddedRecords },
  ref,
) {
  const { t, i18n } = useTranslation("EmployeePortal");
  const locale = (i18n.language as Locale) || "ru";
  const { scan: scanLaborBook, state: ocrState, reset: resetOcr } = useDocumentSuggest();

  const [hasNoExperience, setHasNoExperience] = useState(initialHasNoExperience ?? false);
  const [newEntries, setNewEntries] = useState<ExperienceEntry[]>(() =>
    recordsEditable ? records.map(fromRecord) : (draftAddedRecords ?? []).map(fromRecord),
  );
  const initialSignatureRef = useRef<string>(entriesSignature(recordsEditable ? records.map(fromRecord) : []));
  const [validationErrors, setValidationErrors] = useState<Record<number, Record<string, string>>>({});
  const [workProofFile, setWorkProofFile] = useState<File | null>(null);
  const [workProofRemoved, setWorkProofRemoved] = useState(false);
  const [workProofError, setWorkProofError] = useState<string | null>(null);
  const [enpfFile, setEnpfFile] = useState<File | null>(null);
  const [enpfRemoved, setEnpfRemoved] = useState(false);
  const [enpfError, setEnpfError] = useState<string | null>(null);
  const [ocrToast, setOcrToast] = useState<{ color: "positive" | "notice"; text: string } | null>(null);

  const workFormatOptions = WORK_FORMAT_VALUES.map((v) => ({
    value: v,
    label: t(`personalSheet.workFormatValues.${v}`),
  }));
  const sectorOptions = EXPERIENCE_SECTOR_OPTIONS.map((opt) => ({ label: opt.value, value: opt.value }));
  const monthOptions = getMonthOptions(locale);

  function handleAddEntry() {
    setNewEntries((prev) => [...prev, makeEmptyEntry()]);
  }

  function handleRemoveEntry(index: number) {
    setNewEntries((prev) => prev.filter((_, i) => i !== index));
    setValidationErrors((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  }

  function patchEntry(index: number, patch: Partial<ExperienceEntry>) {
    setNewEntries((prev) => prev.map((entry, i) => (i === index ? { ...entry, ...patch } : entry)));
  }

  function handleWorkProofChange(file: File | null) {
    setWorkProofFile(file);
    if (file) {
      setWorkProofRemoved(false);
      void runLaborBookOcr(file);
    } else {
      resetOcr();
      setOcrToast(null);
    }
  }

  function handleEnpfChange(file: File | null) {
    setEnpfFile(file);
    if (file) setEnpfRemoved(false);
  }

  async function runLaborBookOcr(file: File) {
    const result = await scanLaborBook(file, "employment_record_book");
    if (result.status === "idle") return;
    const scannedIin = result.fields?.iin;
    if (scannedIin && sheetIin && scannedIin !== sheetIin) {
      setOcrToast({ color: "notice", text: "personalSheet.ocr.experience.iinMismatch" });
      return;
    }
    const items = (result.items ?? []) as EmploymentRecordItem[];
    if (items.length === 0) {
      setOcrToast({ color: "notice", text: "personalSheet.ocr.experience.wrongFile" });
      return;
    }
    let addedCount = 0;
    setNewEntries((prev) => {
      const seen = new Set(prev.map(entryDedupKey));
      const additions: ExperienceEntry[] = [];
      for (const item of items) {
        const entry = itemToEntry(item);
        const key = entryDedupKey(entry);
        if (seen.has(key)) continue;
        seen.add(key);
        additions.push(entry);
      }
      addedCount = additions.length;
      return additions.length > 0 ? [...prev, ...additions] : prev;
    });
    setOcrToast(
      addedCount > 0
        ? { color: "positive", text: "personalSheet.ocr.experience.filled" }
        : { color: "notice", text: "personalSheet.ocr.experience.alreadyAdded" },
    );
  }

  useImperativeHandle(ref, function buildHandle() {
    return {
      getChanges() {
        if (hasNoExperience) return [] as unknown as Record<string, unknown>;
        if (recordsEditable) {
          if (entriesSignature(newEntries) === initialSignatureRef.current) return null;
          return newEntries.filter(isFilledEntry).map(toApiEntry) as unknown as Record<string, unknown>;
        }

        const hasNewFilledEntries = newEntries.some(isFilledEntry);

        if (!hasNewFilledEntries) {
          return null;
        }

        const allRecords = [
          ...records.map((r) => ({
            organization_name: r.organization_name,
            speciality: r.speciality,
            work_position: r.work_position,
            work_format: r.work_format,
            work_address: r.work_address ?? "",
            beginning_date: r.beginning_date ?? "",
            end_date: r.end_date ?? "",
            is_current: r.is_current,
          })),
          ...newEntries.filter(isFilledEntry).map((e) => ({
            organization_name: e.organization_name,
            speciality: e.speciality,
            work_position: e.work_position,
            work_format: e.work_format,
            work_address: e.work_address,
            beginning_date: entryBeginDate(e),
            end_date: e.is_current ? "" : entryEndDate(e),
            is_current: e.is_current,
          })),
        ];

        return allRecords as unknown as Record<string, unknown>;
      },
      getFiles() {
        if (hasNoExperience) return null;
        const files: Record<string, File> = {};
        if (workProofFile) files["kyc_documents.work_proof_file"] = workProofFile;
        if (enpfFile) files["kyc_documents.enpf_certificate_file"] = enpfFile;
        return Object.keys(files).length > 0 ? files : null;
      },
      async validate() {
        if (hasNoExperience) {
          setValidationErrors({});
          setWorkProofError(null);
          setEnpfError(null);
          return true;
        }
        const errors: Record<number, Record<string, string>> = {};
        let allValid = true;

        for (let i = 0; i < newEntries.length; i++) {
          const entry = newEntries[i];
          if (!isFilledEntry(entry)) continue;

          const result = editExperienceEntrySchema.safeParse({
            organization_name: entry.organization_name,
            speciality: entry.speciality,
            work_position: entry.work_position,
            work_format: entry.work_format,
            work_address: entry.work_address,
            beginning_date: entryBeginDate(entry),
            end_date: entryEndDate(entry),
          });

          if (!result.success) {
            allValid = false;
            const entryErrors: Record<string, string> = {};
            for (const issue of result.error.issues) {
              const field = issue.path[0] as string;
              entryErrors[field] = issue.message;
            }
            errors[i] = entryErrors;
          }
        }

        setValidationErrors(errors);
        let filesOk = true;
        if (recordsEditable) {
          const workProofOk = (!!workProofUrl && !workProofRemoved) || !!workProofFile;
          setWorkProofError(workProofOk ? null : "personalSheet.validation.required");
          const enpfOk = (!!enpfUrl && !enpfRemoved) || !!enpfFile;
          setEnpfError(enpfOk ? null : "personalSheet.validation.required");
          filesOk = workProofOk && enpfOk;
        } else {
          setWorkProofError(null);
          setEnpfError(null);
        }
        return allValid && filesOk;
      },
      isDirty() {
        const flagChanged = hasNoExperience !== (initialHasNoExperience ?? false);
        const filesChanged = workProofFile !== null || workProofRemoved || enpfFile !== null || enpfRemoved;
        if (recordsEditable) {
          return flagChanged || filesChanged || entriesSignature(newEntries) !== initialSignatureRef.current;
        }
        return flagChanged || filesChanged || newEntries.some(isFilledEntry);
      },
      getFlagChanges() {
        if (hasNoExperience !== (initialHasNoExperience ?? false)) {
          return { has_no_work_experience: hasNoExperience };
        }
        return null;
      },
    };
  });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-4">
        <span className="text-label-sm content-base-secondary">{t("personalSheet.hasNoExperience")}</span>
        <ToggleButton checked={hasNoExperience} onChange={setHasNoExperience} />
      </div>

      {hasNoExperience ? (
        <p className="text-body-regular-sm content-base-secondary p-3 surface-secondary-fill radius-sm">
          {t("personalSheet.noExperienceNote")}
        </p>
      ) : (
        <>
          <FileUploader
            label={t("personalSheet.fields.workProofFile")}
            hasRequiredLabel={recordsEditable}
            existingFileUrl={workProofRemoved ? null : (workProofUrl ?? null)}
            value={workProofFile}
            onChange={handleWorkProofChange}
            onRemoveExisting={() => {
              setWorkProofRemoved(true);
              resetOcr();
              setOcrToast(null);
            }}
            error={workProofError ? t(workProofError) : undefined}
            namespace="EmployeePortal"
          />
          <Toast
            color="grey"
            text={t("personalSheet.fields.laborBookHint")}
            closable={false}
            autoClose={false}
            isFullWidth
          />
          {ocrState === "scanning" && (
            <Toast
              color="grey"
              text={t("personalSheet.ocr.experience.scanning")}
              closable={false}
              autoClose={false}
              isFullWidth
            />
          )}
          {ocrToast && (
            <Toast
              key={`${ocrToast.color}-${ocrToast.text}`}
              color={ocrToast.color}
              text={t(ocrToast.text)}
              isFullWidth
              onClose={() => setOcrToast(null)}
            />
          )}
          <FileUploader
            label={t("personalSheet.fields.enpfFile")}
            hasRequiredLabel={recordsEditable}
            existingFileUrl={enpfRemoved ? null : (enpfUrl ?? null)}
            value={enpfFile}
            onChange={handleEnpfChange}
            onRemoveExisting={() => setEnpfRemoved(true)}
            error={enpfError ? t(enpfError) : undefined}
            namespace="EmployeePortal"
          />

          {!recordsEditable &&
            records.map((record, index) => {
              const begin = parseMonthYear(record.beginning_date);
              const end = parseMonthYear(record.end_date);
              return (
                <div key={record.id} className="p-4 radius-md surface-secondary-fill flex flex-col gap-4">
                  <p className="text-body-bold-md content-base-primary">
                    {t("personalSheet.experienceRecord", { number: index + 1 })}
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input label={t("personalSheet.fields.organizationName")} placeholder="" value={record.organization_name} disabled />
                    <Select label={t("personalSheet.fields.workFormat")} options={workFormatOptions} value={record.work_format} onChange={() => {}} placeholder="" disabled />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Select label={t("personalSheet.fields.specialty")} options={sectorOptions} value={record.speciality} onChange={() => {}} placeholder="" disabled />
                    <Input label={t("personalSheet.fields.workPosition")} placeholder="" value={record.work_position} disabled />
                  </div>

                  <Input label={t("personalSheet.fields.workAddress")} placeholder="" value={record.work_address ?? ""} disabled />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-2">
                      <label className="text-sm leading-5 font-semibold content-base-primary">{t("personalSheet.fields.beginningDate")}</label>
                      <div className="grid grid-cols-[3fr_2fr] gap-4">
                        <Select options={monthOptions} value={begin.month} onChange={() => {}} placeholder={t("personalSheet.fields.month")} disabled />
                        <SearchableSelect<YearOption>
                          options={YEAR_OPTIONS}
                          value={YEAR_OPTIONS.find((y) => y.value === begin.year) ?? null}
                          onChange={() => {}}
                          searchKeys={["label"]}
                          displayKey="label"
                          hideSearchIcon
                          placeholder={t("personalSheet.fields.year")}
                          disabled
                        />
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-sm leading-5 font-semibold content-base-primary">{t("personalSheet.fields.endDate")}</label>
                      <div className="grid grid-cols-[3fr_2fr] gap-4">
                        <Select options={monthOptions} value={end.month} onChange={() => {}} placeholder={t("personalSheet.fields.month")} disabled />
                        <SearchableSelect<YearOption>
                          options={YEAR_OPTIONS}
                          value={YEAR_OPTIONS.find((y) => y.value === end.year) ?? null}
                          onChange={() => {}}
                          searchKeys={["label"]}
                          displayKey="label"
                          hideSearchIcon
                          placeholder={t("personalSheet.fields.year")}
                          disabled
                        />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

          {newEntries.map((entry, index) => (
            <div key={index} className="p-4 radius-md surface-secondary-fill flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <p className="text-body-bold-md content-base-primary">
                  {t("personalSheet.experienceRecord", { number: (recordsEditable ? 0 : records.length) + index + 1 })}
                </p>
                <Button variant="danger" size="sm" onClick={() => handleRemoveEntry(index)}>
                  <Trash size={16} color="currentColor" />
                  {t("personalSheet.removeRecord")}
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label={t("personalSheet.fields.organizationName")}
                  hasRequiredLabel
                  placeholder={t("personalSheet.fields.organizationName")}
                  value={entry.organization_name}
                  onChange={(e) => patchEntry(index, { organization_name: e.target.value })}
                  error={validationErrors[index]?.organization_name ? t(validationErrors[index].organization_name) : undefined}
                />
                <Select
                  label={t("personalSheet.fields.workFormat")}
                  hasRequiredLabel
                  options={workFormatOptions}
                  value={entry.work_format}
                  onChange={(v) => patchEntry(index, { work_format: v ?? "" })}
                  placeholder={t("personalSheet.fields.workFormat")}
                  error={validationErrors[index]?.work_format ? t(validationErrors[index].work_format) : undefined}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Select
                  label={t("personalSheet.fields.specialty")}
                  hasRequiredLabel
                  options={sectorOptions}
                  value={entry.speciality}
                  onChange={(v) => patchEntry(index, { speciality: v ?? "" })}
                  placeholder={t("personalSheet.fields.specialty")}
                  error={validationErrors[index]?.speciality ? t(validationErrors[index].speciality) : undefined}
                />
                <Input
                  label={t("personalSheet.fields.workPosition")}
                  hasRequiredLabel
                  placeholder={t("personalSheet.fields.workPosition")}
                  value={entry.work_position}
                  onChange={(e) => patchEntry(index, { work_position: e.target.value })}
                  error={validationErrors[index]?.work_position ? t(validationErrors[index].work_position) : undefined}
                />
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm leading-5 font-semibold content-base-primary flex items-center gap-1.5">
                  {t("personalSheet.fields.workAddress")}
                  <span className="content-action-negative">*</span>
                  <Tooltip
                    width="w-80"
                    content={
                      <p className="text-label-xs content-action-neutral leading-relaxed">
                        {t("personalSheet.fields.workAddressTooltip")}
                      </p>
                    }>
                    <InfoCircle
                      size={16}
                      color="currentColor"
                      className="content-base-low cursor-pointer hover:opacity-80"
                    />
                  </Tooltip>
                </label>
                <Input
                  placeholder={t("personalSheet.fields.workAddress")}
                  value={entry.work_address}
                  maxLength={500}
                  onChange={(e) => patchEntry(index, { work_address: e.target.value })}
                  error={validationErrors[index]?.work_address ? t(validationErrors[index].work_address) : undefined}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <label className="text-sm leading-5 font-semibold content-base-primary">
                    {t("personalSheet.fields.beginningDate")}
                    <span className="content-action-negative ml-1">*</span>
                  </label>
                  <div className="grid grid-cols-[3fr_2fr] gap-4">
                    <Select
                      options={monthOptions}
                      value={entry.begin_month}
                      onChange={(v) => patchEntry(index, { begin_month: typeof v === "number" ? v : null })}
                      placeholder={t("personalSheet.fields.month")}
                    />
                    <SearchableSelect<YearOption>
                      options={YEAR_OPTIONS}
                      value={YEAR_OPTIONS.find((y) => y.value === entry.begin_year) ?? null}
                      onChange={(opt) => patchEntry(index, { begin_year: opt?.value ?? null })}
                      searchKeys={["label"]}
                      displayKey="label"
                      hideSearchIcon
                      placeholder={t("personalSheet.fields.year")}
                    />
                  </div>
                  {validationErrors[index]?.beginning_date && (
                    <p className="text-body-regular-xs content-action-negative">{t(validationErrors[index].beginning_date)}</p>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-sm leading-5 font-semibold content-base-primary">{t("personalSheet.fields.endDate")}</label>
                  <div className="grid grid-cols-[3fr_2fr] gap-4">
                    <Select
                      options={monthOptions}
                      value={entry.end_month}
                      onChange={(v) => patchEntry(index, { end_month: typeof v === "number" ? v : null })}
                      placeholder={t("personalSheet.fields.month")}
                      disabled={entry.is_current}
                    />
                    <SearchableSelect<YearOption>
                      options={YEAR_OPTIONS}
                      value={YEAR_OPTIONS.find((y) => y.value === entry.end_year) ?? null}
                      onChange={(opt) => patchEntry(index, { end_year: opt?.value ?? null })}
                      searchKeys={["label"]}
                      displayKey="label"
                      hideSearchIcon
                      placeholder={t("personalSheet.fields.year")}
                      disabled={entry.is_current}
                    />
                  </div>
                  {validationErrors[index]?.end_date && (
                    <p className="text-body-regular-xs content-action-negative">{t(validationErrors[index].end_date)}</p>
                  )}
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-label-sm content-base-secondary">{t("personalSheet.fields.currentlyWorking")}</span>
                    <ToggleButton
                      checked={entry.is_current}
                      onChange={(checked) =>
                        patchEntry(index, checked ? { is_current: true, end_month: null, end_year: null } : { is_current: false })
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}

          <Button variant="tertiary" size="lg" onClick={handleAddEntry} className="w-full text-center">
            <Add size={16} color="currentColor" />
            {t("personalSheet.addRecord")}
          </Button>
        </>
      )}
    </div>
  );
});

export default EditExperienceSection;
